import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'

import { ModalSmall } from '@/modules/base/components/modal'
import { ButtonIconSquare, CancelButton } from '@/modules/base/components/buttons'
import TodoService from '@/modules/todo/todoService'
import FileService from '@/modules/file/fileService'
import { type Todo } from '@/modules/todo/types'
import lang from '@/lang'

import TrashIcon from '@assets/images/icons/trashcan.svg'

interface TodoDeleteConfirmationModalProps {
  todo: Todo
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

const useStyles = makeStyles({
  paper: {
    minWidth: 460,
    height: 'auto',
  },
  title: {
    fontSize: 24,
    color: '#414a5f'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: '30px 0px 30px 0px',
    color: '#414a5f',

    '& em': {
      fontWeight: 'bold',
      fontStyle: 'normal',
      wordBreak: 'break-word'
    }
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 10
  }
})

const TodoDeleteConfirmationModal = (props: TodoDeleteConfirmationModalProps) => {
  const { todo, open, onClose, onSuccess } = props
  const classes = useStyles()
  const [isActionDisabled, setIsActionDisabled] = useState(false)
  const { id: userId } = useSelector(data => data.user.info)
  
  const hasDocuments = todo.documents?.length
  const hasFiles = todo.files?.length
  const hasFilesOfOthers = todo.files?.some(file => file.createdBy !== userId)
  let documentFilesDeleteAccess = false
  let showDeleteAnywayButton = false
  let description = lang.todo.detail.deleteModal.deleteConfirmation
  
  if (hasFiles) {
    if (hasFilesOfOthers) {
      description = lang.todo.detail.deleteModal.deleteConfirmationWithoutDocuments
      showDeleteAnywayButton = true
    } else {
      description = lang.todo.detail.deleteModal.deleteConfirmationWithDocuments
      documentFilesDeleteAccess = true
    }
  } else if (hasDocuments) {
    description = lang.todo.detail.deleteModal.deleteConfirmationWithoutDocuments
    showDeleteAnywayButton = true
  }

  const handleDelete = async (deleteDocuments = false) => {
    setIsActionDisabled(true)
    try {
      if (deleteDocuments) {
        if (todo.documents?.length) await Promise.all(todo.documents?.map(doc => TodoService.removeDocument(doc.id)));
        const fileIds = todo.files?.map(file => file.id) ?? []
        if (fileIds.length) await FileService.deleteFiles(fileIds)
      }
      await TodoService.delete(todo.id)
      onSuccess()
    } finally {
      setIsActionDisabled(false)
    }
  }

  return <ModalSmall open={open} onClose={onClose} classes={{ paper: classes.paper }}>
    <span className={classes.title}>{lang.todo.detail.deleteModal.title}</span>
    <div className={classes.content}>{description}</div>
    <div className={classes.footer}>
      <CancelButton
        label={lang.cancelButtonLabel}
        onClick={onClose}
        disabled={isActionDisabled}
        />
      {documentFilesDeleteAccess && <ButtonIconSquare
        variant="outline"
        label={lang.todo.detail.deleteModal.deleteIncludingDocuments}
        onClick={() => handleDelete(true)}
        disabled={isActionDisabled}
      />}
      <ButtonIconSquare
        icon={TrashIcon}
        label={showDeleteAnywayButton ? lang.generics.deleteAnyway : lang.deleteButtonLabel}
        onClick={() => handleDelete()}
        disabled={isActionDisabled}
      />
    </div>
  </ModalSmall>
}

export default TodoDeleteConfirmationModal